import React, { useState, useEffect } from 'react';
import { Save, TrendingUp, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const UpdateStats = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    boxes_reused: '',
    trees_saved: '',
    water_saved: '',
    co2_saved: '',
    energy_saved: '',
    show_full_impact_button: true
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCurrentStats();
  }, []);

  const fetchCurrentStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/impact/stats`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        cache: 'no-store'
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields are filled
    if (!stats.boxes_reused || !stats.trees_saved || !stats.water_saved || !stats.co2_saved || !stats.energy_saved) {
      alert('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/impact/stats/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(stats)
      });

      if (response.ok) {
        alert('Impact statistics updated successfully!');
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error updating stats:', error);
      alert('Failed to update statistics');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-4">
          <Button onClick={() => navigate('/admin/dashboard')} variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">Update Impact Statistics</h1>
            <p className="text-sm text-gray-500">Update environmental impact data displayed on dashboards</p>
          </div>
          <TrendingUp className="w-8 h-8 text-emerald-600" />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <p className="text-gray-600 mb-6">
          Update the environmental impact statistics that will be displayed on the employee dashboard and impact page.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Boxes Reused */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Boxes Reused <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={stats.boxes_reused}
              onChange={(e) => setStats({ ...stats, boxes_reused: e.target.value })}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 text-lg"
              placeholder="e.g., 10,000 or 10K"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Enter as text (e.g., "10,000" or "10K+")</p>
          </div>

          {/* Trees Saved */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of Trees Saved <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={stats.trees_saved}
              onChange={(e) => setStats({ ...stats, trees_saved: e.target.value })}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 text-lg"
              placeholder="e.g., 500 or 0.5K"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Approximate number of trees saved</p>
          </div>

          {/* Water Saved */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Litres of Water Saved <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={stats.water_saved}
              onChange={(e) => setStats({ ...stats, water_saved: e.target.value })}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 text-lg"
              placeholder="e.g., 50,000L or 50KL"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Total litres of water saved</p>
          </div>

          {/* CO2 Saved */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tonnes CO₂ Saved <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={stats.co2_saved}
              onChange={(e) => setStats({ ...stats, co2_saved: e.target.value })}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 text-lg"
              placeholder="e.g., 2.5 tonnes or 2,500kg"
              required
            />
            <p className="text-xs text-gray-500 mt-1">CO₂ emissions prevented in tonnes</p>
          </div>

          {/* Energy Saved */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              KwH Energy Saved <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={stats.energy_saved}
              onChange={(e) => setStats({ ...stats, energy_saved: e.target.value })}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 text-lg"
              placeholder="e.g., 1,000 kWh or 1 MWh"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Total energy saved in kilowatt-hours</p>
          </div>

          {/* Show Full Impact Button Toggle */}
          <div className="pt-6 border-t">
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
              <input
                type="checkbox"
                id="show_full_impact_button"
                checked={stats.show_full_impact_button}
                onChange={(e) => setStats({ ...stats, show_full_impact_button: e.target.checked })}
                className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
              />
              <label htmlFor="show_full_impact_button" className="text-sm font-medium text-gray-900 cursor-pointer">
                Show "See Full Impact" button on employee portal
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-2 ml-1">When enabled, employees can click to see the full impact page. Disable to hide the button without redeploying.</p>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-6">
            <button
              type="submit"
              disabled={loading}
              className="flex items-center px-8 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 font-semibold"
            >
              <Save className="w-5 h-5 mr-2" />
              {loading ? 'Saving...' : 'Save Statistics'}
            </button>
          </div>
        </form>

        {/* Preview */}
        <div className="mt-8 pt-8 border-t">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
          <p className="text-sm text-gray-600 mb-4">This is how the statistics will appear on the employee dashboard:</p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-emerald-50 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-emerald-900">{stats.boxes_reused || '0'}</div>
              <div className="text-sm text-emerald-700 mt-1">Boxes Reused</div>
            </div>
            
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-green-900">{stats.trees_saved || '0'}</div>
              <div className="text-sm text-green-700 mt-1">Trees Saved</div>
            </div>
            
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-blue-900">{stats.water_saved || '0'}</div>
              <div className="text-sm text-blue-700 mt-1">Litres Water Saved</div>
            </div>
            
            <div className="bg-purple-50 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-purple-900">{stats.co2_saved || '0'}</div>
              <div className="text-sm text-purple-700 mt-1">Tonnes CO₂ Saved</div>
            </div>
            
            <div className="bg-orange-50 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-orange-900">{stats.energy_saved || '0'}</div>
              <div className="text-sm text-orange-700 mt-1">KwH Energy Saved</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateStats;
